'use client'
import { use, useDeferredValue, useEffect, useRef, useState } from "react";

export function ClientLoadable<TParams, TResponse>({ render, fetch, params, dependencies = [], empty, emptyOnEmptyArray, onIntercept }: {
    params: TParams,
    fetch: (params?: TParams) => Promise<TResponse>,
    render: (value: TResponse) => React.ReactNode | null;
    dependencies?: any[];
    empty?: React.ReactNode,
    emptyOnEmptyArray?: boolean;
    onIntercept?: (response: TResponse) => void;
}) {
    const [promise, setPromise] = useState<Promise<TResponse>>();
    const deferredPromise = useDeferredValue(promise)
    const fetched = useRef<string>(null);
    useEffect(() => {
        const unique = (JSON.stringify(dependencies) || '')
        if (fetched.current == unique)
            return;
        fetched.current = unique
        setPromise(fetch(params).then(r => {
            if (onIntercept)
                onIntercept(r)
            fetched.current = null
            return r
        }))
    }, dependencies);

    if (!deferredPromise) {
        return empty
    }

    const data = use(deferredPromise)

    if (data == undefined || (emptyOnEmptyArray && Array.isArray(data) && data.length == 0 && empty != undefined)) {
        return empty;
    }

    return render(data);
}

export function useClientLoadable<TParams, TResponse>(fetch: (params?: TParams) => Promise<TResponse>, params: TParams, dependencies?: any[]) {
    const [data, setData] = useState<TResponse>();
    const [loading, setLoading] = useState(true);
    const fetching = useRef<Promise<void>>(null); // ✅ Prevent double fetch

    useEffect(() => {
        (async () => {
            if (fetching.current){
                await fetching.current
            }
            setLoading(true);
            const promise = fetch(params).then(setData)
            fetching.current = promise
            promise.then(() => {
                if (fetching.current == promise) {
                    fetching.current = null;
                    setLoading(false);
                }
            });
        })()
    
        fetch(params).then(setData).then(() => {
            setLoading(false);
        });
    }, dependencies ?? []);

    return [data, loading]
}